import { useEffect, useState } from 'react'
import {
  Container,
  CircularProgress,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Box,
} from '@mui/material'
import Masonry from 'react-masonry-css'
import { useAuthContext } from '../../hooks/useAuthContext'
import ProcessTemplateCard from '../../components/Dashboard/ProcessTemplateCard'
import { processTemplateService } from '../../services/processTemplateService'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import { CATEGORY_CONTEXT } from '../../utility/constants'

export default function MyProcesses() {
  const { user } = useAuthContext()
  const [isLoading, setIsLoading] = useState(false)
  const [processCategoryDetails, setProcessCategoryDetails] = useState({})
  const [allCategories, setAllCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState({ id: -1, name: 'All' })
  const [isPreferenceFetched, setPreferencesFetched] = useState(false)
  const { chip } = useResponsiveTypography()
  const getDataForCategory = async () => {
    try {
      setIsLoading(true)
      const response = await processTemplateService.getProcessForCategory(user)
      const result = response.data
      setProcessCategoryDetails(result)
      const allCategory = result?.processCategories.reduce((acc, cur) => {
        return acc.find((it) => it.id == cur.process_category_id) == undefined
          ? [...acc, { id: cur.process_category_id, name: cur.name }]
          : acc
      }, [])

      setAllCategories(allCategory)

      const userCategory = result?.userSavedProcessCategories?.find(
        (it) => it.user_id == user.id && it.category_context == CATEGORY_CONTEXT.MY_PROCESSES
      )
      if (userCategory) {
        const matchedCategory = allCategory.find(
          (cat) => cat.id === userCategory.process_category_id
        )
        setSelectedCategory(
          matchedCategory || { id: userCategory.process_category_id, name: 'All' }
        )
      }

      setPreferencesFetched(true)
      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching processes:', error)
    }
  }

  useEffect(() => {
    if (user) {
      getDataForCategory()
    }
  }, [user])

  const breakpoints = {
    default: 4,
    1100: 2,
    700: 1,
  }

  useEffect(() => {
    ;(async () => {
      if (isPreferenceFetched) {
        await processTemplateService.savedProcessCategories(
          user,
          selectedCategory?.id,
          CATEGORY_CONTEXT.MY_PROCESSES
        )
      }
    })()
  }, [selectedCategory])

  const handleCategoryChange = (event) => {
    const selectedId = event.target.value
    if (selectedId === -1) {
      setSelectedCategory({ id: -1, name: 'All' })
    } else {
      const selected = allCategories.find((cat) => cat.id === selectedId)
      if (selected) {
        setSelectedCategory(selected)
      }
    }
  }

  const getAllowInitiatorOrViewer = (processTemplate) => {
    let allowedIntiatorIds = processTemplate.allowed_initiator_team_ids.split(',')
    let allowedViewerIds = processTemplate.allowed_viewer_team_ids.split(',')
    let userTeamIds = user.teams.join().split(',')
    let allowedIntiateUser = userTeamIds.some((ai) => allowedIntiatorIds.includes(ai))
    let allowedViewUser = userTeamIds.some((ai) => allowedViewerIds.includes(ai))
    return { allowedIntiateUser, allowedViewUser }
  }

  return (
    <Container
      className="main-container process-dashabord"
      style={{ paddingLeft: '12px', paddingRight: '4px' }}
    >
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh',
            padding: '10px 10px',
            margin: '5px auto',
          }}
        >
          <CircularProgress color="primary" />
        </div>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={2}
            style={{
              marginBottom: '1rem',
              alignItems: 'center',
              marginLeft: -6,
            }}
          >
            <FormControl size="small" style={{ minWidth: 200 }}>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                value={selectedCategory.id}
                label="Category"
                onChange={handleCategoryChange}
                style={{ borderRadius: '50px' }}
              >
                <MenuItem value={-1}>All</MenuItem>
                {allCategories.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <section className="processcard-sec">
            <Masonry
              breakpointCols={breakpoints}
              className="my-masonry-grid"
              columnClassName="my-masonry-grid_column"
            >
              {processCategoryDetails?.processCategories
                ?.filter((it, index, self) => {
                  const allowInitiatorOrViewer = getAllowInitiatorOrViewer(it)
                  return (
                    (selectedCategory?.id === -1 ||
                      it.process_category_id === selectedCategory?.id) &&
                    (selectedCategory?.id === -1 ||
                      index === self.findIndex((t) => t.id === it.id)) &&
                    (allowInitiatorOrViewer.allowedIntiateUser ||
                      allowInitiatorOrViewer.allowedViewUser)
                  )
                })
                .map((processTemplate) => {
                  const allowInitiatorOrViewer = getAllowInitiatorOrViewer(processTemplate)

                  return (
                    <div
                      key={processTemplate.id}
                      className="templateCard"
                      style={{ marginBottom: 5 }}
                    >
                      <ProcessTemplateCard
                        processTemplate={processTemplate}
                        allowIntiateProcess={allowInitiatorOrViewer.allowedIntiateUser}
                        allowViewProcess={allowInitiatorOrViewer.allowedViewUser}
                      />
                    </div>
                  )
                })}
            </Masonry>
          </section>
        </>
      )}
    </Container>
  )
}
